import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, X } from 'lucide-react';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';
import {
    PE_PAPERS_A4_LETTER_PREVIEW_CLASS,
    PE_PAPERS_DOC_P_CLASS,
    PE_PAPERS_NOBLE_PRINT_LETTER_CSS,
    PE_PAPERS_NOBLE_SLATE_LETTER_CSS,
} from './shared/nobleSlateDocumentStyles';

type TeamPool = 'produktiv' | 'unproduktiv' | 'azubi';

type TeamEmployeeOption = {
    id: string;
    name: string;
    pool: TeamPool;
};

interface KuendigungModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

type DokumentTyp = 'kuendigung' | 'aufhebungsvertrag';
type Kuendigungsart = 'ordentlich' | 'ausserordentlich';
type UrlaubRegelung = 'freizeit' | 'auszahlung';

interface BetriebFormData {
    firmenname: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface MitarbeiterFormData {
    name: string;
    strasse: string;
    plz: string;
    ort: string;
}

type KuendigungsgrundKey =
    | 'pflichtverletzung'
    | 'arbeitsverweigerung'
    | 'diebstahl'
    | 'taetlichkeit'
    | 'sonstiger'
    | '';

interface KuendigungDetails {
    kuendigungsart: Kuendigungsart;
    datumKuendigungsschreiben: string;
    datumLetzterArbeitstag: string;
    kuendigungsgrund: KuendigungsgrundKey;
    kuendigungsgrundFreitext: string;
}

interface AufhebungsvertragDetails {
    datumVertragsunterzeichnung: string;
    datumAusscheiden: string;
    /** Be-/verrechnete Urlaubstage für § 3 Aufhebungsvertrag */
    urlaubstageVerrechnet: number;
}

interface BetriebFieldLocks {
    firmenname: boolean;
    strasse: boolean;
    plz: boolean;
    ort: boolean;
}

const INITIAL_MITARBEITER_DATA: MitarbeiterFormData = {
    name: '',
    strasse: '',
    plz: '',
    ort: '',
};

const INITIAL_KUENDIGUNG_DETAILS: KuendigungDetails = {
    kuendigungsart: 'ordentlich',
    datumKuendigungsschreiben: '',
    datumLetzterArbeitstag: '',
    kuendigungsgrund: '',
    kuendigungsgrundFreitext: '',
};

const KUENDIGUNGSGRUND_OPTIONS: { key: Exclude<KuendigungsgrundKey, ''>; label: string }[] = [
    { key: 'pflichtverletzung', label: 'Schwerwiegende Pflichtverletzung (§ 626 BGB)' },
    { key: 'arbeitsverweigerung', label: 'Anhaltende, unberechtigte Arbeitsverweigerung' },
    {
        key: 'diebstahl',
        label: 'Diebstahl / Unterschlagung / Vermögensdelikt zum Nachteil des Betriebes',
    },
    {
        key: 'taetlichkeit',
        label: 'Tätlichkeit / schwerwiegende Beleidigung gegen Vorgesetzte, Kollegen oder Kunden',
    },
    { key: 'sonstiger', label: 'Sonstiger wichtiger Grund' },
];

const INITIAL_AUFHEBUNG_DETAILS: AufhebungsvertragDetails = {
    datumVertragsunterzeichnung: '',
    datumAusscheiden: '',
    urlaubstageVerrechnet: 0,
};


const getOrdentlicheUrlaubText = (regelung: UrlaubRegelung, verhaeltnissesGenitiv = 'Arbeitsverhältnisses'): string =>
    regelung === 'freizeit'
        ? 'Ihre verbleibenden Resturlaubsansprüche sowie etwaige Überstunden werden bis zum Beendigungszeitpunkt durch Freizeitgewährung unter Fortzahlung der Vergütung vollumfänglich abgegolten.'
        : `Noch bestehende Resturlaubsansprüche sowie etwaige Überstunden werden mit dem Ende des ${verhaeltnissesGenitiv} berechnet, finanziell abgegolten und mit der letzten Entgeltabrechnung ausbezahlt.`;

const getAufhebungUrlaubText = (regelung: UrlaubRegelung, tage: number): string => {
    const x = Number.isFinite(tage) ? String(Math.max(0, Math.trunc(tage))) : '0';
    return regelung === 'freizeit'
        ? `${x} Urlaubstage werden bis zum Beendigungszeitpunkt unter Fortzahlung der Vergütung in Freizeit gewährt.`
        : `${x} Urlaubstage verbleiben und werden mit der Endabrechnung finanziell abgegolten.`;
};

const A4_PREVIEW_PAGE_CLASS = PE_PAPERS_A4_LETTER_PREVIEW_CLASS;
const DOC_P_CLASS = PE_PAPERS_DOC_P_CLASS;

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const READONLY_INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-accent-text dark:text-[#E6C299] px-4 py-2.5 text-sm cursor-not-allowed opacity-90';

const ERROR_INPUT_RING = 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/20';

const STEP_LABELS: Record<number, string> = {
    1: 'Datenerfassung & Typ-Weiche',
    2: 'Vorschau & Export',
};

const isEmpty = (value: string) => !String(value || '').trim();

/** Legacy-Archiv: einzeilige `adresse` → Straße / PLZ / Ort */
const parseLegacyMitarbeiterAdresse = (
    adresse: string
): Pick<MitarbeiterFormData, 'strasse' | 'plz' | 'ort'> => {
    const raw = String(adresse || '').trim();
    if (!raw) return { strasse: '', plz: '', ort: '' };

    const lines = raw
        .split(/[\n;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

    const plzOrtMatch = (value: string) => {
        const m = value.match(/^(\d{4,5})\s+(.+)$/);
        return m ? { plz: m[1], ort: m[2].trim() } : null;
    };

    if (lines.length === 1) {
        const parts = lines[0]
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (parts.length >= 2) {
            const last = plzOrtMatch(parts[parts.length - 1]);
            if (last) {
                return {
                    strasse: parts.slice(0, -1).join(', '),
                    plz: last.plz,
                    ort: last.ort,
                };
            }
        }
        const single = plzOrtMatch(lines[0]);
        if (single) return { strasse: '', ...single };
        return { strasse: lines[0], plz: '', ort: '' };
    }

    const lastLine = plzOrtMatch(lines[lines.length - 1]);
    if (lastLine) {
        return {
            strasse: lines.slice(0, -1).join(', '),
            plz: lastLine.plz,
            ort: lastLine.ort,
        };
    }
    return { strasse: lines[0], plz: '', ort: lines.slice(1).join(' ') };
};

const normalizeMitarbeiterData = (incoming: any): MitarbeiterFormData => {
    const src = incoming && typeof incoming === 'object' ? incoming : {};
    const hasStructured =
        !isEmpty(String(src.strasse || '')) ||
        !isEmpty(String(src.plz || '')) ||
        !isEmpty(String(src.ort || ''));
    if (!hasStructured && typeof src.adresse === 'string' && src.adresse.trim()) {
        const parsed = parseLegacyMitarbeiterAdresse(src.adresse);
        return {
            name: String(src.name || ''),
            strasse: parsed.strasse,
            plz: parsed.plz,
            ort: parsed.ort,
        };
    }
    return {
        name: String(src.name || ''),
        strasse: String(src.strasse || ''),
        plz: String(src.plz || ''),
        ort: String(src.ort || ''),
    };
};

const formatMitarbeiterAdresseInline = (data: MitarbeiterFormData): string => {
    const line1 = data.strasse.trim();
    const line2 = [data.plz.trim(), data.ort.trim()].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join(', ');
};

const formatDisplayDate = (value: string): string => {
    if (!value.trim()) return '____________';
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
    return value.trim();
};

const formatTodayGerman = (): string => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
};

const buildBetriebFromClient = (clientData: any): { betrieb: BetriebFormData; locks: BetriebFieldLocks } => {
    const firmenname = clientData?.salonname || clientData?.firmenname || '';
    const strasse = clientData?.strasse || '';
    const plz = clientData?.plz || '';
    const ort = clientData?.ort || '';

    return {
        betrieb: { firmenname, strasse, plz, ort },
        locks: {
            firmenname: !isEmpty(firmenname),
            strasse: !isEmpty(strasse),
            plz: !isEmpty(plz),
            ort: !isEmpty(ort),
        },
    };
};

const getKuendigungsgrundLabel = (key: KuendigungsgrundKey): string => {
    const match = KUENDIGUNGSGRUND_OPTIONS.find((opt) => opt.key === key);
    return match?.label || '____________';
};

const getKuendigungsgrundDisplayText = (details: KuendigungDetails): string => {
    if (details.kuendigungsgrund === 'sonstiger') {
        return details.kuendigungsgrundFreitext.trim() || '____________';
    }
    return getKuendigungsgrundLabel(details.kuendigungsgrund);
};

const sanitizeKuendigungsgrundForSentence = (value: string): string => {
    if (!value.trim() || value === '____________') return value;
    return value.trim().replace(/\.+$/, '');
};

const KuendigungModal: React.FC<KuendigungModalProps> = ({ isOpen, onClose, initialData, onArchived }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [dokumentTyp, setDokumentTyp] = useState<DokumentTyp>('kuendigung');
    const [istAuszubildender, setIstAuszubildender] = useState(false);
    const [betriebData, setBetriebData] = useState<BetriebFormData>({
        firmenname: '',
        strasse: '',
        plz: '',
        ort: '',
    });
    const [betriebLocks, setBetriebLocks] = useState<BetriebFieldLocks>({
        firmenname: false,
        strasse: false,
        plz: false,
        ort: false,
    });
    const [mitarbeiterData, setMitarbeiterData] = useState<MitarbeiterFormData>(INITIAL_MITARBEITER_DATA);
    const [selectedTeamEmployeeId, setSelectedTeamEmployeeId] = useState('');
    const [kuendigungDetails, setKuendigungDetails] = useState<KuendigungDetails>(INITIAL_KUENDIGUNG_DETAILS);
    const [aufhebungDetails, setAufhebungDetails] = useState<AufhebungsvertragDetails>(INITIAL_AUFHEBUNG_DETAILS);
    const [urlaubRegelung, setUrlaubRegelung] = useState<UrlaubRegelung>('freizeit');
    const [sonderparagraph, setSonderparagraph] = useState('');
    const [rueckgabeGegenstaende, setRueckgabeGegenstaende] = useState('');
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [disclaimerPulse, setDisclaimerPulse] = useState(false);

    const printContainerRef = useRef<HTMLDivElement>(null);
    const disclaimerBoxRef = useRef<HTMLDivElement>(null);
    const kuendigungSchreibenRef = useRef<HTMLInputElement>(null);
    const kuendigungLetzterTagRef = useRef<HTMLInputElement>(null);
    const aufhebungUnterzeichnungRef = useRef<HTMLInputElement>(null);
    const aufhebungAusscheidenRef = useRef<HTMLInputElement>(null);

    const clientData = useAppStore((state: any) => state.clientData);
    const { personalData, unproductivePersonalData, azubiAssistenzData } = usePersonalDataSelector();

    const teamEmployees = useMemo((): TeamEmployeeOption[] => {
        const mapPool = (list: any[], pool: TeamPool): TeamEmployeeOption[] =>
            (Array.isArray(list) ? list : [])
                .filter((emp) => emp?.id)
                .map((emp) => ({
                    id: String(emp.id),
                    name: String(emp.name || '').trim() || 'Ohne Name',
                    pool,
                }));
        return [
            ...mapPool(personalData, 'produktiv'),
            ...mapPool(unproductivePersonalData, 'unproduktiv'),
            ...mapPool(azubiAssistenzData, 'azubi'),
        ];
    }, [personalData, unproductivePersonalData, azubiAssistenzData]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setDokumentTyp('kuendigung');
            setIstAuszubildender(false);
            setMitarbeiterData(INITIAL_MITARBEITER_DATA);
            setSelectedTeamEmployeeId('');
            setKuendigungDetails(INITIAL_KUENDIGUNG_DETAILS);
            setAufhebungDetails(INITIAL_AUFHEBUNG_DETAILS);
            setUrlaubRegelung('freizeit');
            setSonderparagraph('');
            setRueckgabeGegenstaende('');
            setInvalidFields(new Set());
            setDisclaimerAccepted(false);
            setIsSaving(false);
            setArchiveFeedback('idle');
            setArchiveDocId(null);
            return;
        }

        const { betrieb, locks } = buildBetriebFromClient(clientData);
        setBetriebData(betrieb);
        setBetriebLocks(locks);
        setCurrentStep(1);
        setDokumentTyp('kuendigung');
        setIstAuszubildender(false);
        setMitarbeiterData(INITIAL_MITARBEITER_DATA);
        setSelectedTeamEmployeeId('');
        setKuendigungDetails(INITIAL_KUENDIGUNG_DETAILS);
        setAufhebungDetails(INITIAL_AUFHEBUNG_DETAILS);
        setUrlaubRegelung('freizeit');
        setSonderparagraph('');
        setRueckgabeGegenstaende('');
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(initialData?.id || null);

        if (initialData) {
            if (initialData.dokumentTyp === 'kuendigung' || initialData.dokumentTyp === 'aufhebungsvertrag') {
                setDokumentTyp(initialData.dokumentTyp);
            }
            setIstAuszubildender(Boolean(initialData.istAuszubildender));
            if (initialData.betriebData && typeof initialData.betriebData === 'object') {
                setBetriebData({ ...betrieb, ...initialData.betriebData });
                setBetriebLocks({
                    firmenname: !isEmpty(String(initialData.betriebData.firmenname ?? betrieb.firmenname)),
                    strasse: !isEmpty(String(initialData.betriebData.strasse ?? betrieb.strasse)),
                    plz: !isEmpty(String(initialData.betriebData.plz ?? betrieb.plz)),
                    ort: !isEmpty(String(initialData.betriebData.ort ?? betrieb.ort)),
                });
            }
            if (initialData.mitarbeiterData && typeof initialData.mitarbeiterData === 'object') {
                setMitarbeiterData(normalizeMitarbeiterData(initialData.mitarbeiterData));
            }
            if (typeof initialData.selectedTeamEmployeeId === 'string') {
                setSelectedTeamEmployeeId(initialData.selectedTeamEmployeeId);
            }
            if (initialData.kuendigungDetails && typeof initialData.kuendigungDetails === 'object') {
                setKuendigungDetails({ ...INITIAL_KUENDIGUNG_DETAILS, ...initialData.kuendigungDetails });
            }
            if (initialData.aufhebungDetails && typeof initialData.aufhebungDetails === 'object') {
                setAufhebungDetails({ ...INITIAL_AUFHEBUNG_DETAILS, ...initialData.aufhebungDetails });
            }
            if (typeof initialData.urlaubstageVerrechnet === 'number') {
                setAufhebungDetails((prev) => ({
                    ...prev,
                    urlaubstageVerrechnet: initialData.urlaubstageVerrechnet,
                }));
            }
            if (initialData.urlaubRegelung === 'freizeit' || initialData.urlaubRegelung === 'auszahlung') {
                setUrlaubRegelung(initialData.urlaubRegelung);
            }
            if (typeof initialData.sonderparagraph === 'string') {
                setSonderparagraph(initialData.sonderparagraph);
            }
            if (typeof initialData.rueckgabeGegenstaende === 'string' && initialData.rueckgabeGegenstaende.trim()) {
                setRueckgabeGegenstaende(initialData.rueckgabeGegenstaende);
            }
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) {
                setCurrentStep(step);
            }
            if (initialData.confirmedDisclaimer || initialData.status === 'final') {
                setDisclaimerAccepted(true);
            }
        }
    }, [isOpen, clientData, initialData]);

    // iOS: Hintergrund per position:fixed sperren — touch-action:none blockiert sonst Modal-Scroll
    useEffect(() => {
        if (!isOpen) return;
        const scrollY = window.scrollY;
        const prev = {
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width,
            htmlOverflow: document.documentElement.style.overflow,
        };
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        return () => {
            document.documentElement.style.overflow = prev.htmlOverflow;
            document.body.style.overflow = prev.overflow;
            document.body.style.position = prev.position;
            document.body.style.top = prev.top;
            document.body.style.width = prev.width;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    const validateStep1 = (): string[] => {
        const errors: string[] = [];

        if (isEmpty(betriebData.firmenname)) errors.push('betrieb_firmenname');
        if (isEmpty(betriebData.strasse)) errors.push('betrieb_strasse');
        if (isEmpty(betriebData.plz)) errors.push('betrieb_plz');
        if (isEmpty(betriebData.ort)) errors.push('betrieb_ort');

        if (isEmpty(mitarbeiterData.name)) errors.push('mitarbeiter_name');
        if (isEmpty(mitarbeiterData.strasse)) errors.push('mitarbeiter_strasse');
        if (isEmpty(mitarbeiterData.plz)) errors.push('mitarbeiter_plz');
        if (isEmpty(mitarbeiterData.ort)) errors.push('mitarbeiter_ort');

        if (dokumentTyp === 'kuendigung') {
            if (isEmpty(kuendigungDetails.datumKuendigungsschreiben)) errors.push('kuendigung_datum_schreiben');
            if (isEmpty(kuendigungDetails.datumLetzterArbeitstag)) errors.push('kuendigung_datum_letzter_tag');
            if (kuendigungDetails.kuendigungsart === 'ausserordentlich') {
                if (!kuendigungDetails.kuendigungsgrund) errors.push('kuendigung_grund');
                if (
                    kuendigungDetails.kuendigungsgrund === 'sonstiger' &&
                    isEmpty(kuendigungDetails.kuendigungsgrundFreitext)
                ) {
                    errors.push('kuendigung_grund_freitext');
                }
            }
        } else {
            if (isEmpty(aufhebungDetails.datumVertragsunterzeichnung)) errors.push('aufhebung_datum_unterzeichnung');
            if (isEmpty(aufhebungDetails.datumAusscheiden)) errors.push('aufhebung_datum_ausscheiden');
        }

        return errors;
    };

    const isFieldInvalid = (field: string) => invalidFields.has(field);

    const getInputClass = (baseClass: string, field: string) =>
        isFieldInvalid(field) ? `${baseClass} ${ERROR_INPUT_RING}` : baseClass;

    const clearFieldError = (field: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const updateBetriebField = <K extends keyof BetriebFormData>(key: K, value: BetriebFormData[K]) => {
        setBetriebData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`betrieb_${key}`);
    };

    const updateMitarbeiterField = <K extends keyof MitarbeiterFormData>(key: K, value: MitarbeiterFormData[K]) => {
        setMitarbeiterData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`mitarbeiter_${key}`);
    };

    const handleTeamEmployeeSelect = (employeeId: string) => {
        setSelectedTeamEmployeeId(employeeId);
        if (!employeeId) return;
        const emp = teamEmployees.find((e) => e.id === employeeId);
        if (!emp) return;
        setMitarbeiterData((prev) => ({
            ...prev,
            name: emp.name === 'Ohne Name' ? '' : emp.name,
        }));
        clearFieldError('mitarbeiter_name');
        if (emp.pool === 'azubi') {
            setIstAuszubildender(true);
        }
    };

    const updateKuendigungField = <K extends keyof KuendigungDetails>(key: K, value: KuendigungDetails[K]) => {
        setKuendigungDetails((prev) => ({ ...prev, [key]: value }));
        if (key === 'datumKuendigungsschreiben') clearFieldError('kuendigung_datum_schreiben');
        if (key === 'datumLetzterArbeitstag') clearFieldError('kuendigung_datum_letzter_tag');
        if (key === 'kuendigungsgrund') {
            clearFieldError('kuendigung_grund');
            clearFieldError('kuendigung_grund_freitext');
        }
        if (key === 'kuendigungsgrundFreitext') clearFieldError('kuendigung_grund_freitext');
    };

    const handleKuendigungsartChange = (art: Kuendigungsart) => {
        if (art === 'ordentlich') {
            setKuendigungDetails((prev) => ({
                ...prev,
                kuendigungsart: art,
                kuendigungsgrund: '',
                kuendigungsgrundFreitext: '',
            }));
            clearFieldError('kuendigung_grund');
            clearFieldError('kuendigung_grund_freitext');
            return;
        }
        updateKuendigungField('kuendigungsart', art);
    };

    const updateAufhebungField = <K extends keyof AufhebungsvertragDetails>(
        key: K,
        value: AufhebungsvertragDetails[K]
    ) => {
        setAufhebungDetails((prev) => ({ ...prev, [key]: value }));
        if (key === 'datumVertragsunterzeichnung') clearFieldError('aufhebung_datum_unterzeichnung');
        if (key === 'datumAusscheiden') clearFieldError('aufhebung_datum_ausscheiden');
    };

    const renderFieldError = (field: string) =>
        isFieldInvalid(field) ? (
            <p className="text-[10px] text-red-400 mt-1">Dieses Feld ist erforderlich</p>
        ) : null;

    const openDatePicker = (ref: React.RefObject<HTMLInputElement | null>) => {
        const el = ref.current;
        if (!el) return;
        if (typeof el.showPicker === 'function') {
            el.showPicker();
        } else {
            el.focus();
        }
    };

    const renderDateField = (
        label: string,
        value: string,
        onChange: (nextValue: string) => void,
        fieldId: string,
        inputRef: React.RefObject<HTMLInputElement | null>
    ) => (
        <div>
            <label className={LABEL_CLASS}>{label}</label>
            <div
                className="relative flex items-center cursor-pointer"
                onClick={() => openDatePicker(inputRef)}
            >
                <input
                    ref={inputRef}
                    type="date"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={getInputClass(`${INPUT_CLASS} pr-10 cursor-pointer`, fieldId)}
                />
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        openDatePicker(inputRef);
                    }}
                    className="absolute right-3 text-pe-muted dark:text-slate-400 hover:text-[#E6C299] transition-colors"
                    aria-label="Datum wählen"
                >
                    <Calendar className="w-4 h-4" />
                </button>
            </div>
            {renderFieldError(fieldId)}
        </div>
    );

    const handleDokumentTypChange = (typ: DokumentTyp) => {
        setDokumentTyp(typ);
        setInvalidFields(new Set());
    };

    const handleWeiter = () => {
        const errors = validateStep1();
        if (errors.length > 0) {
            setInvalidFields(new Set(errors));
            return;
        }
        setInvalidFields(new Set());
        setCurrentStep(2);
    };

    const handleZurueck = () => {
        setInvalidFields(new Set());
        setCurrentStep(1);
    };

    const handleClose = () => {
        setCurrentStep(1);
        setDokumentTyp('kuendigung');
        setIstAuszubildender(false);
        setMitarbeiterData(INITIAL_MITARBEITER_DATA);
        setSelectedTeamEmployeeId('');
        setKuendigungDetails(INITIAL_KUENDIGUNG_DETAILS);
        setAufhebungDetails(INITIAL_AUFHEBUNG_DETAILS);
        setUrlaubRegelung('freizeit');
        setSonderparagraph('');
        setRueckgabeGegenstaende('');
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        onClose();
    };

    const sonderparagraphText = sonderparagraph.trim();
    const hasSonderparagraph = sonderparagraphText.length > 0;
    const rueckgabeText = rueckgabeGegenstaende.trim();
    const hasRueckgabe = rueckgabeText.length > 0;

    const isActionLocked = !disclaimerAccepted;

    const getActionButtonClass = (baseClass: string) =>
        `${baseClass} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const verhaeltnis = istAuszubildender ? 'Ausbildungsverhältnis' : 'Arbeitsverhältnis';
    const verhaeltnisses = istAuszubildender ? 'Ausbildungsverhältnisses' : 'Arbeitsverhältnisses';
    const dokumentTitel =
        dokumentTyp === 'kuendigung'
            ? istAuszubildender
                ? 'Kündigung Ausbildungsverhältnis'
                : 'Kündigungsschreiben'
            : istAuszubildender
              ? 'Aufhebungsvertrag für Auszubildende'
              : 'Aufhebungsvertrag';
    const kuendigungBetreff = istAuszubildender
        ? 'Betreff: Kündigung Ausbildungsverhältnis'
        : 'Betreff: Kündigung des Arbeitsverhältnisses';
    const modalHeaderTitle =
        dokumentTyp === 'kuendigung'
            ? istAuszubildender
                ? 'Kündigung Ausbildungsverhältnis'
                : 'Kündigung / Aufhebungsvertrag'
            : istAuszubildender
              ? 'Aufhebungsvertrag für Auszubildende'
              : 'Kündigung / Aufhebungsvertrag';

    const betriebAddressLine = [betriebData.strasse, [betriebData.plz, betriebData.ort].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');

    const getDocumentHtml = () =>
        printContainerRef.current?.innerHTML ||
        document.getElementById('pepapers-kuendigung-print')?.innerHTML ||
        '';

    const focusDisclaimerGate = () => {
        setDisclaimerPulse(true);
        disclaimerBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => setDisclaimerPulse(false), 2200);
    };

    const handlePrintIsolated = () => {
        if (isActionLocked) {
            focusDisclaimerGate();
            return;
        }

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        const printWindow = iframe.contentWindow;
        if (!printWindow) {
            document.body.removeChild(iframe);
            return;
        }

        const doc = printWindow.document;
        const printContent = getDocumentHtml();

        doc.open();
        doc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${dokumentTitel}</title>
      <style>${PE_PAPERS_NOBLE_PRINT_LETTER_CSS}</style>
    </head>
    <body>
      ${printContent}
    </body>
    </html>
  `);
        doc.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setTimeout(() => {
                if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                }
            }, 1000);
        }, 500);
    };

    const handleDownloadPdf = async () => {
        if (isActionLocked) {
            focusDisclaimerGate();
            return;
        }
        if (isPdfDownloading) return;

        const html = getDocumentHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }

        setIsPdfDownloading(true);
        try {
            const safeTitle = dokumentTitel.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_');
            const dateIso = new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `${safeTitle}_${dateIso}.pdf`,
                extraCss: PE_PAPERS_NOBLE_SLATE_LETTER_CSS,
                fitSectionsToPage: true,
                capturePadPx: 0,
            });
        } catch (err: any) {
            console.error('PEPapers Kündigung PDF-Download fehlgeschlagen:', err);
            const msg = String(err?.message || '');
            const friendly =
                /setEnd|Range|offset/i.test(msg)
                    ? 'PDF-Erstellung ist fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
                    : msg || 'Unbekannter Fehler';
            window.alert('PDF konnte nicht heruntergeladen werden: ' + friendly);
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) {
            focusDisclaimerGate();
            return;
        }

        setIsSaving(true);
        setArchiveFeedback('idle');

        const htmlContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-kuendigung-print')?.innerHTML ||
            '';

        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'kuendigung-aufhebungsvertrag',
                status: asDraft ? 'entwurf' : 'final',
                salonName: betriebData.firmenname || 'Unbekannter Salon',
                mitarbeiterName: mitarbeiterData.name.trim(),
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    dokumentTyp,
                    istAuszubildender,
                    betriebData,
                    mitarbeiterData: {
                        ...mitarbeiterData,
                        adresse: formatMitarbeiterAdresseInline(mitarbeiterData),
                    },
                    selectedTeamEmployeeId: selectedTeamEmployeeId || null,
                    kuendigungDetails: dokumentTyp === 'kuendigung' ? kuendigungDetails : null,
                    aufhebungDetails: dokumentTyp === 'aufhebungsvertrag' ? aufhebungDetails : null,
                    urlaubRegelung,
                    urlaubstageVerrechnet:
                        dokumentTyp === 'aufhebungsvertrag'
                            ? aufhebungDetails.urlaubstageVerrechnet
                            : null,
                    sonderparagraph: sonderparagraphText || null,
                    rueckgabeGegenstaende: hasRueckgabe ? rueckgabeText : null,
                    generatedHtml: htmlContent,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => {
                    onClose();
                }, 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers Kündigung Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const renderSegmentedControl = <T extends string>(
        value: T,
        options: { key: T; label: string }[],
        onChange: (key: T) => void
    ) => (
        <div className="flex rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-1 gap-1">
            {options.map((opt) => (
                <button
                    key={opt.key}
                    type="button"
                    onClick={() => onChange(opt.key)}
                    className={`flex-1 px-3 py-2 rounded-md text-[10px] font-bold uppercase tracking-widest transition-colors ${
                        value === opt.key
                            ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                            : 'text-stone-500 dark:text-slate-500 hover:text-stone-700 dark:hover:text-slate-300 border border-transparent'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );

    const renderUrlaubRegelungField = () => (
        <div>
            <label className={LABEL_CLASS}>Urlaubs- und Überstundenregelung</label>
            {renderSegmentedControl<UrlaubRegelung>(
                urlaubRegelung,
                [
                    { key: 'freizeit', label: 'Durch Freizeitgewährung/Freistellung abgelten' },
                    { key: 'auszahlung', label: 'Finanziell abgelten und ausbezahlen' },
                ],
                setUrlaubRegelung
            )}
        </div>
    );

    const renderRueckgabeField = () => (
        <div>
            <label className={LABEL_CLASS}>Rückgabe / Erhalt von (manuell anpassbar)</label>
            <textarea
                value={rueckgabeGegenstaende}
                onChange={(e) => setRueckgabeGegenstaende(e.target.value)}
                rows={3}
                placeholder="z. B. Salon-Schlüssel, Werkzeuge, Arbeitskleidung, Zugangskarten …"
                className={`${INPUT_CLASS} resize-y min-h-[72px]`}
            />
            <p className="text-[10px] text-pe-muted dark:text-slate-500 mt-1.5 leading-relaxed">
                Wird im Dokument als Liste der zurückzugebenden Gegenstände übernommen.
            </p>
        </div>
    );

    const renderSonderparagraphField = () => (
        <div>
            <label className={LABEL_CLASS}>Sonderparagraph (optional, frei editierbar)</label>
            <textarea
                value={sonderparagraph}
                onChange={(e) => setSonderparagraph(e.target.value)}
                rows={4}
                placeholder={
                    'z. B. Sonderregelung zu Arbeitsmaterial, Resturlaub, Firmenwagen, Kundendaten …\n\nNur ausfüllen, wenn zusätzlich etwas vereinbart werden soll.'
                }
                className={`${INPUT_CLASS} resize-y min-h-[100px]`}
            />
            <p className="text-[10px] text-pe-muted dark:text-slate-500 mt-1.5 leading-relaxed">
                Wird nur ins Dokument übernommen, wenn Text vorhanden ist
                {dokumentTyp === 'aufhebungsvertrag' ? ' (als eigener Paragraph).' : '.'}
            </p>
        </div>
    );

    const renderSonderparagraphInDocument = (variant: 'letter' | 'vertrag') => {
        if (!hasSonderparagraph) return null;

        if (variant === 'vertrag') {
            return (
                <div className="av-doc-section">
                    <h3>§ 7 Sonderregelungen</h3>
                    <p className={`${DOC_P_CLASS} whitespace-pre-wrap`}>{sonderparagraphText}</p>
                </div>
            );
        }

        return (
            <p className={`${DOC_P_CLASS} whitespace-pre-wrap`}>
                <strong>Sonderregelung:</strong> {sonderparagraphText}
            </p>
        );
    };

    const renderBetriebField = (field: keyof BetriebFormData, label: string) => {
        const lockKey = field as keyof BetriebFieldLocks;
        const fieldId = `betrieb_${field}`;
        const isLocked = betriebLocks[lockKey];

        return (
            <div>
                <label className={LABEL_CLASS}>{label}</label>
                <input
                    type="text"
                    value={betriebData[field]}
                    readOnly={isLocked}
                    onChange={(e) => !isLocked && updateBetriebField(field, e.target.value)}
                    className={getInputClass(isLocked ? READONLY_INPUT_CLASS : INPUT_CLASS, fieldId)}
                />
                {renderFieldError(fieldId)}
            </div>
        );
    };

    const schreibenDatumAnzeige =
        formatDisplayDate(kuendigungDetails.datumKuendigungsschreiben) !== '____________'
            ? formatDisplayDate(kuendigungDetails.datumKuendigungsschreiben)
            : formatTodayGerman();

    const renderKuendigungLetterChrome = (title: string, subtitle: string) => (
        <>
            <div className="pdf-letterhead text-center">
                <h1>{title}</h1>
                <h2>{subtitle}</h2>
            </div>
            <div className="av-meta-grid">
                <div className="av-meta-col">
                    <p className="av-meta-label">Arbeitgeber</p>
                    <p>
                        <strong>{betriebData.firmenname || '____________'}</strong>
                        {betriebData.strasse ? (
                            <>
                                <br />
                                {betriebData.strasse}
                            </>
                        ) : null}
                        {betriebData.plz || betriebData.ort ? (
                            <>
                                <br />
                                {[betriebData.plz, betriebData.ort].filter(Boolean).join(' ')}
                            </>
                        ) : null}
                        <span className="av-meta-date">Datum: {schreibenDatumAnzeige}</span>
                    </p>
                </div>
                <div className="av-meta-col">
                    <p className="av-meta-label">{istAuszubildender ? 'Auszubildende(r)' : 'Arbeitnehmer/in'}</p>
                    <p>
                        <strong>{mitarbeiterData.name || '____________'}</strong>
                        {mitarbeiterData.strasse ? (
                            <>
                                <br />
                                {mitarbeiterData.strasse}
                            </>
                        ) : null}
                        {mitarbeiterData.plz || mitarbeiterData.ort ? (
                            <>
                                <br />
                                {[mitarbeiterData.plz, mitarbeiterData.ort].filter(Boolean).join(' ')}
                            </>
                        ) : !mitarbeiterData.strasse ? (
                            <>
                                <br />
                                ____________
                            </>
                        ) : null}
                    </p>
                </div>
            </div>
        </>
    );

    const renderKuendigungSignatures = () => (
        <table className="ab-sig-table">
            <tbody>
                <tr>
                    <td>
                        <p>
                            <strong>Ort, Datum, Unterschrift Arbeitgeber/in</strong>
                        </p>
                        <span className="ab-sig-line-soft" />
                        <p>{betriebData.firmenname || '____________'}</p>
                    </td>
                    <td>
                        <p>
                            <strong>Empfangsbestätigung Mitarbeiter/in</strong>
                        </p>
                        <p>Datum: _________________________</p>
                        <span className="ab-sig-line-soft" />
                        <p>{mitarbeiterData.name || '____________'}</p>
                    </td>
                </tr>
            </tbody>
        </table>
    );

    const renderOrdentlicheKuendigungDocument = () => (
        <div className={A4_PREVIEW_PAGE_CLASS}>
            {renderKuendigungLetterChrome(dokumentTitel, 'PreisExpert · PEPapers')}
            <div className="av-doc-section">
                <h3>{kuendigungBetreff.replace(/^Betreff:\s*/i, '')}</h3>
                <p className={DOC_P_CLASS}>
                    Sehr geehrte/r <strong>{mitarbeiterData.name || '____________'}</strong>,
                </p>
                <p className={DOC_P_CLASS}>
                    hiermit kündigen wir das zwischen Ihnen und unserem Salon bestehende {verhaeltnis} ordentlich und
                    fristgerecht zum <strong>{formatDisplayDate(kuendigungDetails.datumLetzterArbeitstag)}</strong>.
                </p>
                <p className={DOC_P_CLASS}>
                    Das Kündigungsschreiben ist datiert auf den{' '}
                    <strong>{formatDisplayDate(kuendigungDetails.datumKuendigungsschreiben)}</strong>. Wir weisen Sie
                    ausdrücklich darauf hin, dass Sie gemäß § 38 Abs. 1 SGB III verpflichtet sind, sich innerhalb von drei
                    Tagen nach Erhalt dieses Schreibens persönlich oder online bei der Agentur für Arbeit arbeitslos zu
                    melden, sofern Sie Anspruch auf Leistungen der Arbeitslosenversicherung geltend machen möchten.
                </p>
                <p className={DOC_P_CLASS}>{getOrdentlicheUrlaubText(urlaubRegelung, verhaeltnisses)}</p>
                {hasRueckgabe && (
                    <p className={DOC_P_CLASS}>
                        Bitte übergeben Sie uns bis zum{' '}
                        <strong>{formatDisplayDate(kuendigungDetails.datumLetzterArbeitstag)}</strong> sämtliches in Ihrem
                        Besitz befindliche Saloneigentum im Original und funktionstüchtig, insbesondere: {rueckgabeText}.
                    </p>
                )}
                {renderSonderparagraphInDocument('letter')}
                <p className={DOC_P_CLASS}>Wir bitten um eine schriftliche Empfangsbestätigung dieses Schreibens.</p>
            </div>
            <div className="av-letter-closing mt-auto">
                <p className={DOC_P_CLASS}>Mit freundlichen Grüßen</p>
                <p className={DOC_P_CLASS}>
                    <strong>{betriebData.firmenname || '____________'}</strong>
                </p>
                {renderKuendigungSignatures()}
            </div>
        </div>
    );

    const renderAusserordentlicheKuendigungDocument = () => {
        const kuendigungsgrundText = sanitizeKuendigungsgrundForSentence(
            getKuendigungsgrundDisplayText(kuendigungDetails)
        );
        const kuendigungSchreibenDatum = kuendigungDetails.datumKuendigungsschreiben.trim()
            ? formatDisplayDate(kuendigungDetails.datumKuendigungsschreiben)
            : formatTodayGerman();

        return (
            <div className={A4_PREVIEW_PAGE_CLASS}>
                {renderKuendigungLetterChrome(dokumentTitel, 'PreisExpert · PEPapers')}
                <div className="av-doc-section">
                    <h3>{kuendigungBetreff.replace(/^Betreff:\s*/i, '')}</h3>
                    <p className={DOC_P_CLASS}>
                        Sehr geehrte/r <strong>{mitarbeiterData.name || '____________'}</strong>,
                    </p>
                    <p className={DOC_P_CLASS}>
                        hiermit kündigen wir das zwischen Ihnen und unserem Salon bestehende {verhaeltnis} außerordentlich
                        und fristlos aus wichtigem Grund gemäß § 626 BGB mit sofortiger Wirkung (zum{' '}
                        <strong>{kuendigungSchreibenDatum}</strong>), ersatzweise zum nächstmöglichen Zeitpunkt.
                    </p>
                    <p className={DOC_P_CLASS}>
                        Als wichtiger Grund für diese außerordentliche Kündigung wird folgende rechtliche Verfehlung
                        angeführt: <strong>{kuendigungsgrundText}</strong>. Ein Festhalten am {verhaeltnis} bis zum Ablauf
                        der ordentlichen Kündigungsfrist ist dem Arbeitgeber unter Abwägung aller Interessen nicht zumutbar.
                    </p>
                    <p className={DOC_P_CLASS}>
                        Wir weisen Sie ausdrücklich darauf hin, dass Sie gemäß § 38 Abs. 1 SGB III verpflichtet sind, sich
                        innerhalb von drei Tagen nach Erhalt dieses Schreibens persönlich oder online bei der Agentur für
                        Arbeit arbeitslos zu melden. Weiterhin besteht die Pflicht zur aktiven Suche nach einer Beschäftigung.
                    </p>
                    {hasRueckgabe && (
                        <p className={DOC_P_CLASS}>
                            Bitte übergeben Sie uns bis zum <strong>{kuendigungSchreibenDatum}</strong> sämtliches in Ihrem
                            Besitz befindliche Saloneigentum im Original und funktionstüchtig, insbesondere: {rueckgabeText}.
                        </p>
                    )}
                    {renderSonderparagraphInDocument('letter')}
                    <p className={DOC_P_CLASS}>
                        Wir bitten um umgehende schriftliche Empfangsbestätigung auf der beigefügten Zweitschrift dieses
                        Dokuments.
                    </p>
                </div>
                <div className="av-letter-closing mt-auto">
                    <p className={DOC_P_CLASS}>Mit freundlichen Grüßen</p>
                    <p className={DOC_P_CLASS}>
                        <strong>{betriebData.firmenname || '____________'}</strong>
                    </p>
                    {renderKuendigungSignatures()}
                </div>
            </div>
        );
    };

    const renderKuendigungDocument = () =>
        kuendigungDetails.kuendigungsart === 'ordentlich'
            ? renderOrdentlicheKuendigungDocument()
            : renderAusserordentlicheKuendigungDocument();

    const renderAufhebungsvertragDocument = () => {
        let para = 1;
        const nextPara = () => para++;
        const p1 = nextPara();
        const pRueckgabe = hasRueckgabe ? nextPara() : null;
        const pUrlaub = nextPara();
        const pSozial = nextPara();
        const pAbgeltung = nextPara();
        const pVerschwiegenheit = nextPara();
        const pSonder = hasSonderparagraph ? nextPara() : null;

        return (
        <div className={A4_PREVIEW_PAGE_CLASS}>
            {renderKuendigungLetterChrome(
                dokumentTitel,
                `Beendigung des ${verhaeltnisses} im gegenseitigen Einvernehmen · PEPapers`
            )}
            <div className="av-doc-section">
                <h3>Präambel</h3>
                <p className={DOC_P_CLASS}>
                    Zwischen <strong>{betriebData.firmenname || '____________'}</strong>
                    {betriebAddressLine ? `, ${betriebAddressLine}` : ''} (nachfolgend „Arbeitgeber“) und{' '}
                    <strong>{mitarbeiterData.name || '____________'}</strong>, wohnhaft in{' '}
                    <strong>{formatMitarbeiterAdresseInline(mitarbeiterData) || '____________'}</strong>{' '}
                    (nachfolgend {istAuszubildender ? '„Auszubildende(r)“' : '„Arbeitnehmer/in“'}) wird folgende
                    Aufhebungsvereinbarung geschlossen:
                </p>
            </div>
            <div className="av-doc-section">
                <h3>§ {p1} Beendigungszeitpunkt</h3>
                <p className={DOC_P_CLASS}>
                    Die Vertragsparteien sind sich einig, das zwischen ihnen bestehende {verhaeltnis} im gegenseitigen
                    Einvernehmen zum <strong>{formatDisplayDate(aufhebungDetails.datumAusscheiden)}</strong> zu beenden.
                    Die Unterzeichnung dieses Vertrages erfolgt am{' '}
                    <strong>{formatDisplayDate(aufhebungDetails.datumVertragsunterzeichnung)}</strong>.
                </p>
            </div>
            {pRueckgabe !== null && (
                <div className="av-doc-section">
                    <h3>§ {pRueckgabe} Ordnungsgemäße Übergabe</h3>
                    <p className={DOC_P_CLASS}>
                        Der/die {istAuszubildender ? 'Auszubildende' : 'Arbeitnehmer/in'} verpflichtet sich, vor dem
                        Ausscheiden folgende Gegenstände des Arbeitgebers vollständig, im Original und funktionstüchtig
                        zurückzugeben: {rueckgabeText}.
                    </p>
                </div>
            )}
            <div className="av-doc-section">
                <h3>§ {pUrlaub} Resturlaub &amp; Überstunden</h3>
                <p className={DOC_P_CLASS}>
                    {getAufhebungUrlaubText(urlaubRegelung, aufhebungDetails.urlaubstageVerrechnet)}
                </p>
            </div>
            <div className="av-doc-section">
                <h3>§ {pSozial} Sozialversicherungsrechtliche Hinweise</h3>
                <p className={DOC_P_CLASS}>
                    Der Arbeitnehmer wird darauf hingewiesen, dass der Abschluss des Aufhebungsvertrags zu
                    sozialversicherungsrechtlichen Nachteilen führen kann, insbesondere beim Bezug von Arbeitslosengeld
                    (Sperrzeit/Ruhen des Anspruchs). Abschließende rechtsverbindliche Auskünfte sind den jeweiligen
                    Sozialversicherungsträgern vorbehalten (Bundesagentur für Arbeit u. a.). Zur Aufrechterhaltung
                    ungekürzter Ansprüche auf Arbeitslosengeld ist der Arbeitnehmer nach § 38 Abs. 1 SGB III verpflichtet,
                    sich spätestens drei Tage nach Kenntnis des Beendigungszeitraums bei der Agentur für Arbeit persönlich
                    als arbeitsuchend zu melden. Der Arbeitnehmer wird zudem darauf hingewiesen, dass er eigene Aktivitäten
                    bei der Suche nach einer anderen Beschäftigung entfalten muss.
                </p>
            </div>
            <div className="av-doc-section">
                <h3>§ {pAbgeltung} Abgeltungs- und Erledigungsklausel / Schlussbestimmungen / Salvatorische Klausel</h3>
                <p className={DOC_P_CLASS}>
                    Mit dieser Vereinbarung sind sämtliche Ansprüche aus dem Vertragsverhältnis, seiner Beendigung und für
                    die Zeit nach Beendigung – gleich aus welchem Rechtsgrund und ob bekannt oder unbekannt – abgegolten und
                    erledigt, soweit sich aus dieser Vereinbarung nichts anderes ergibt.
                </p>
                <p className={DOC_P_CLASS}>
                    Sollte eine Bestimmung dieser Vereinbarung unwirksam sein, wird die Wirksamkeit der übrigen Bestimmungen
                    hiervon nicht berührt. Die Parteien verpflichten sich, die unwirksamen Bestimmungen durch eine
                    Vereinbarung zu ersetzen, die der unwirksamen Bestimmung in Interessenlage und Bedeutung möglichst nahe
                    kommt. Entsprechendes gilt für den Fall, dass die Regelungen dieses Vertrages eine von den
                    Vertragsparteien nicht beabsichtigte Lücke aufweisen.
                </p>
            </div>
            <div className="av-doc-section">
                <h3>§ {pVerschwiegenheit} Verschwiegenheit</h3>
                <p className={DOC_P_CLASS}>
                    Beide Vertragsparteien verpflichten sich zur absoluten Geheimhaltung von Salon-Rezepturen, internen
                    Betriebsdaten, Kundenlisten sowie sämtlichen geschäfts- und betriebsbezogenen Informationen auch nach
                    Beendigung des {verhaeltnisses}.
                </p>
            </div>
            {pSonder !== null
                ? (
                    <div className="av-doc-section">
                        <h3>§ {pSonder} Sonderregelungen</h3>
                        <p className={`${DOC_P_CLASS} whitespace-pre-wrap`}>{sonderparagraphText}</p>
                    </div>
                )
                : null}
            <div className="av-letter-closing mt-auto">
                <p className={DOC_P_CLASS}>
                    Ort, Datum: <strong>{formatDisplayDate(aufhebungDetails.datumVertragsunterzeichnung)}</strong>
                </p>
                <table className="ab-sig-table">
                    <tbody>
                        <tr>
                            <td>
                                <span className="ab-sig-line-soft" />
                                <p>Unterschrift Arbeitgeber/in</p>
                                <p>{betriebData.firmenname}</p>
                            </td>
                            <td>
                                <span className="ab-sig-line-soft" />
                                <p>Unterschrift {istAuszubildender ? 'Auszubildende(r)' : 'Arbeitnehmer/in'}</p>
                                <p>{mitarbeiterData.name}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        );
    };

    const renderContractDocument = () => (
        <div ref={printContainerRef} id="pepapers-kuendigung-print" className="w-full h-auto overflow-visible">
            <style>{PE_PAPERS_NOBLE_SLATE_LETTER_CSS}</style>
            {dokumentTyp === 'kuendigung' ? renderKuendigungDocument() : renderAufhebungsvertragDocument()}
        </div>
    );

    if (!isOpen) return null;

    const modalWidthClass = currentStep === 2 ? 'max-w-4xl' : 'max-w-lg';

    const stopBackgroundScroll = (e: React.TouchEvent) => {
        e.preventDefault();
    };

    return createPortal(
        <>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden touch-none"
                onClick={handleClose}
                onTouchMove={stopBackgroundScroll}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[4.5rem] sm:top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-stretch justify-center overflow-hidden p-2 sm:p-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} h-full max-h-full flex min-h-0 flex-col overflow-hidden relative transition-all duration-200 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:h-auto print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-3 right-3 sm:top-4 sm:right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg p-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] z-50 shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-4 sm:px-6 pt-8 sm:pt-10 pb-3 sm:pb-4">
                            <h3 className="text-base sm:text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8">
                                {modalHeaderTitle}
                            </h3>
                            <p className="text-center text-[10px] sm:text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div
                        className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain touch-pan-y px-4 sm:px-6 py-4 sm:py-5 custom-scrollbar print:p-0 print:overflow-visible"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Betriebsdaten
                                    </p>
                                    <div className="space-y-4">
                                        {renderBetriebField('firmenname', 'Firma / Salonname')}
                                        {renderBetriebField('strasse', 'Straße & Hausnummer')}
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            {renderBetriebField('plz', 'PLZ')}
                                            <div className="sm:col-span-2">{renderBetriebField('ort', 'Ort')}</div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Mitarbeiter-Daten
                                    </p>
                                    <div className="space-y-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Aus Team wählen</label>
                                            <select
                                                value={selectedTeamEmployeeId}
                                                onChange={(e) => handleTeamEmployeeSelect(e.target.value)}
                                                className={INPUT_CLASS}
                                            >
                                                <option value="">Manuell eintragen</option>
                                                {teamEmployees.map((emp) => (
                                                    <option key={`${emp.pool}-${emp.id}`} value={emp.id}>
                                                        {emp.name}
                                                        {emp.pool === 'azubi'
                                                            ? ' (Azubi/Assistenz)'
                                                            : emp.pool === 'unproduktiv'
                                                              ? ' (unproduktiv)'
                                                              : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="mt-1 text-[10px] text-pe-muted dark:text-slate-500">
                                                Name kann nach der Auswahl noch ergänzt werden (z.&nbsp;B. Nachname).
                                            </p>
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Name des Mitarbeiters</label>
                                            <input
                                                type="text"
                                                value={mitarbeiterData.name}
                                                onChange={(e) => updateMitarbeiterField('name', e.target.value)}
                                                className={getInputClass(INPUT_CLASS, 'mitarbeiter_name')}
                                            />
                                            {renderFieldError('mitarbeiter_name')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Straße &amp; Hausnummer</label>
                                            <input
                                                type="text"
                                                value={mitarbeiterData.strasse}
                                                onChange={(e) => updateMitarbeiterField('strasse', e.target.value)}
                                                placeholder="z. B. Musterstraße 12"
                                                className={getInputClass(INPUT_CLASS, 'mitarbeiter_strasse')}
                                            />
                                            {renderFieldError('mitarbeiter_strasse')}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div>
                                                <label className={LABEL_CLASS}>PLZ</label>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={mitarbeiterData.plz}
                                                    onChange={(e) => updateMitarbeiterField('plz', e.target.value)}
                                                    placeholder="12345"
                                                    className={getInputClass(INPUT_CLASS, 'mitarbeiter_plz')}
                                                />
                                                {renderFieldError('mitarbeiter_plz')}
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label className={LABEL_CLASS}>Ort</label>
                                                <input
                                                    type="text"
                                                    value={mitarbeiterData.ort}
                                                    onChange={(e) => updateMitarbeiterField('ort', e.target.value)}
                                                    placeholder="Musterstadt"
                                                    className={getInputClass(INPUT_CLASS, 'mitarbeiter_ort')}
                                                />
                                                {renderFieldError('mitarbeiter_ort')}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Dokumenten-Typ
                                    </p>
                                    {renderSegmentedControl<DokumentTyp>(
                                        dokumentTyp,
                                        [
                                            { key: 'kuendigung', label: 'Kündigung' },
                                            { key: 'aufhebungsvertrag', label: 'Aufhebungsvertrag' },
                                        ],
                                        handleDokumentTypChange
                                    )}
                                    <label className="mt-3 flex items-center gap-2.5 cursor-pointer select-none rounded-lg border border-pe-border dark:border-slate-700/80 bg-stone-100/60 dark:bg-black/40 px-3 py-2.5">
                                        <input
                                            type="checkbox"
                                            checked={istAuszubildender}
                                            onChange={(e) => setIstAuszubildender(e.target.checked)}
                                            className="h-4 w-4 rounded border-pe-border-strong dark:border-slate-600 bg-pe-input dark:bg-black text-pe-accent-text dark:text-[#E6C299] focus:ring-[#E6C299]/40 focus:ring-offset-0"
                                        />
                                        <span className="text-xs font-semibold text-pe-muted dark:text-slate-300">
                                            Für Auszubildende(n)
                                        </span>
                                    </label>
                                </div>

                                {dokumentTyp === 'kuendigung' ? (
                                    <div className="space-y-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Kündigungsart</label>
                                            {renderSegmentedControl<Kuendigungsart>(
                                                kuendigungDetails.kuendigungsart,
                                                [
                                                    { key: 'ordentlich', label: 'Ordentlich fristgerecht' },
                                                    { key: 'ausserordentlich', label: 'Außerordentlich fristlos' },
                                                ],
                                                handleKuendigungsartChange
                                            )}
                                        </div>
                                        {kuendigungDetails.kuendigungsart === 'ausserordentlich' && (
                                            <>
                                                <div>
                                                    <label className={LABEL_CLASS}>Kündigungsgrund</label>
                                                    <select
                                                        value={kuendigungDetails.kuendigungsgrund}
                                                        onChange={(e) =>
                                                            updateKuendigungField(
                                                                'kuendigungsgrund',
                                                                e.target.value as KuendigungsgrundKey
                                                            )
                                                        }
                                                        className={getInputClass(INPUT_CLASS, 'kuendigung_grund')}
                                                    >
                                                        <option value="">Bitte wählen …</option>
                                                        {KUENDIGUNGSGRUND_OPTIONS.map((opt) => (
                                                            <option key={opt.key} value={opt.key}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {renderFieldError('kuendigung_grund')}
                                                </div>
                                                {kuendigungDetails.kuendigungsgrund === 'sonstiger' && (
                                                    <div>
                                                        <label className={LABEL_CLASS}>
                                                            Sonstiger wichtiger Grund (Freitext)
                                                        </label>
                                                        <textarea
                                                            value={kuendigungDetails.kuendigungsgrundFreitext}
                                                            onChange={(e) =>
                                                                updateKuendigungField(
                                                                    'kuendigungsgrundFreitext',
                                                                    e.target.value
                                                                )
                                                            }
                                                            rows={3}
                                                            className={getInputClass(
                                                                `${INPUT_CLASS} resize-y min-h-[80px]`,
                                                                'kuendigung_grund_freitext'
                                                            )}
                                                        />
                                                        {renderFieldError('kuendigung_grund_freitext')}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {renderDateField(
                                            'Datum des Kündigungsschreibens',
                                            kuendigungDetails.datumKuendigungsschreiben,
                                            (v) => updateKuendigungField('datumKuendigungsschreiben', v),
                                            'kuendigung_datum_schreiben',
                                            kuendigungSchreibenRef
                                        )}
                                        {renderDateField(
                                            'Letzter Arbeitstag / Beendigungszeitpunkt',
                                            kuendigungDetails.datumLetzterArbeitstag,
                                            (v) => updateKuendigungField('datumLetzterArbeitstag', v),
                                            'kuendigung_datum_letzter_tag',
                                            kuendigungLetzterTagRef
                                        )}
                                        {kuendigungDetails.kuendigungsart === 'ordentlich' && renderUrlaubRegelungField()}
                                        {renderRueckgabeField()}
                                        {renderSonderparagraphField()}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {renderDateField(
                                            'Datum der Vertragsunterzeichnung',
                                            aufhebungDetails.datumVertragsunterzeichnung,
                                            (v) => updateAufhebungField('datumVertragsunterzeichnung', v),
                                            'aufhebung_datum_unterzeichnung',
                                            aufhebungUnterzeichnungRef
                                        )}
                                        {renderDateField(
                                            'Datum des rechtlichen Ausscheidens',
                                            aufhebungDetails.datumAusscheiden,
                                            (v) => updateAufhebungField('datumAusscheiden', v),
                                            'aufhebung_datum_ausscheiden',
                                            aufhebungAusscheidenRef
                                        )}
                                        {renderUrlaubRegelungField()}
                                        <div>
                                            <label className={LABEL_CLASS}>
                                                Be-/verrechnete Urlaubstage
                                            </label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={aufhebungDetails.urlaubstageVerrechnet}
                                                onChange={(e) =>
                                                    updateAufhebungField(
                                                        'urlaubstageVerrechnet',
                                                        Math.max(0, Math.trunc(Number(e.target.value) || 0))
                                                    )
                                                }
                                                className={INPUT_CLASS}
                                            />
                                        </div>
                                        {renderRueckgabeField()}
                                        {renderSonderparagraphField()}
                                    </div>
                                )}
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="w-full min-h-0 pr-1 sm:pr-2 mb-2">
                                {/* Mobil: Vorschau begrenzt, damit Hinweis + Aktionen erreichbar bleiben */}
                                <div className="max-h-[min(52vh,520px)] sm:max-h-none overflow-y-auto overscroll-y-contain rounded-lg border border-pe-border/60 dark:border-[#333] bg-stone-100/40 dark:bg-black/20 p-2 sm:p-0 sm:border-0 sm:bg-transparent">
                                    {renderContractDocument()}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="shrink-0 max-h-[min(52vh,460px)] overflow-y-auto overscroll-y-contain touch-pan-y border-t border-pe-border dark:border-[#222] print:hidden bg-pe-card dark:bg-[#111]">
                    {currentStep === 2 && (
                        <div
                            ref={disclaimerBoxRef}
                            className={`px-4 sm:px-6 pt-3 transition-shadow duration-300 ${
                                disclaimerPulse
                                    ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-pe-card dark:ring-offset-[#111] rounded-xl'
                                    : ''
                            }`}
                        >
                            <div
                                className={`rounded-xl border-2 transition-all duration-200 ${
                                    disclaimerAccepted
                                        ? 'border-emerald-500/40 bg-emerald-500/5 px-3 py-2'
                                        : 'border-amber-500 bg-amber-500/15 dark:bg-amber-500/10 shadow-[0_0_24px_rgba(245,158,11,0.25)] p-3 sm:p-4'
                                }`}
                            >
                                {!disclaimerAccepted && (
                                    <>
                                        <p className="mb-1.5 sm:mb-2 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-amber-800 dark:text-amber-300">
                                            Pflicht · Rechtlicher Hinweis bestätigen
                                        </p>
                                        <p className="mb-2 sm:mb-3 text-[11px] sm:text-xs font-semibold text-amber-950 dark:text-amber-100/95 leading-snug">
                                            Ohne diese Bestätigung bleiben PDF-Download, Druck und Archiv-Speichern gesperrt.
                                        </p>
                                    </>
                                )}
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={disclaimerAccepted}
                                        onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                        className="mt-0.5 w-5 h-5 shrink-0 cursor-pointer appearance-none bg-pe-input dark:bg-black border-2 border-amber-600 dark:border-amber-400 rounded checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors after:content-['✔'] after:absolute after:text-black after:text-[11px] after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                                    />
                                    <span
                                        className={
                                            disclaimerAccepted
                                                ? 'text-xs sm:text-sm font-semibold text-emerald-800 dark:text-emerald-300 leading-snug'
                                                : 'text-xs sm:text-sm text-pe-text dark:text-slate-200 leading-relaxed group-hover:text-black dark:group-hover:text-white transition-colors'
                                        }
                                    >
                                        {disclaimerAccepted
                                            ? 'Rechtlicher Hinweis bestätigt'
                                            : 'Ich habe die Daten geprüft und bestätige ausdrücklich, dass ich den rechtlichen Hinweis verstanden habe. Mir ist bekannt, dass es sich hierbei um ein automatisiertes Standard-Muster und keine individuelle Rechtsberatung handelt. Die Verwendung erfolgt auf eigene Gefahr und Verantwortung.'}
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="px-4 sm:px-6 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
                        {currentStep === 2 && isActionLocked && (
                            <button
                                type="button"
                                onClick={focusDisclaimerGate}
                                className="mb-3 w-full rounded-lg border border-amber-500/60 bg-amber-500/20 px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200 hover:bg-amber-500/30 transition-colors"
                            >
                                ↑ Bitte den rechtlichen Hinweis anhaken
                            </button>
                        )}
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={handleZurueck}
                                    className="w-full sm:flex-1 sm:min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors"
                                >
                                    Zurück
                                </button>
                            )}

                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="w-full sm:flex-1 sm:min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="w-full sm:flex-1 sm:min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="w-full sm:flex-1 sm:min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isSaving}
                                        className={getActionButtonClass(
                                            'w-full sm:flex-1 sm:min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        {isSaving && archiveFeedback !== 'draft'
                                            ? 'Speichert...'
                                            : archiveFeedback === 'success'
                                            ? 'Gespeichert! ✓'
                                            : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isPdfDownloading}
                                        className={getActionButtonClass(
                                            'w-full sm:flex-1 sm:min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-colors disabled:opacity-50'
                                        )}
                                    >
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrintIsolated}
                                        className={getActionButtonClass(
                                            'w-full sm:flex-1 sm:min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        Ausdrucken
                                    </button>
                                </>
                            )}
                        </div>
                        {currentStep === 2 && archiveFeedback === 'error' && (
                            <p className="text-[10px] text-red-400 mt-2 text-center">
                                Archivierung fehlgeschlagen. Bitte erneut versuchen.
                            </p>
                        )}
                    </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default KuendigungModal;
